import { useMemo } from "react";
import cx from "classnames";

import TablePagination from "components/Table/Pagination";
import TableSimple from "ds/components/TableSimple";
import { IntentResourceOperation, IntentResourceOperationState } from "types/generated";
import usePagination from "components/Table/Pagination/usePagination";
import useIntentProjectOperationsStore from "shared/IntentProject/useSearchIntentResourcesOperations/store";

import useIntentProjectHistoryTableStore from "./useIntentProjectHistoryTableStore";
import {
  INTENT_PROJECT_HISTORY_LIMIT,
  INTENT_PROJECT_HISTORY_LIMIT_STORAGE_KEY,
  INTENT_PROJECT_HISTORY_TABLE_FILTERS_STORAGE_KEY,
  INTENT_PROJECT_HISTORY_PAGINATION_OPTIONS,
  INTENT_PROJECT_HISTORY_SORT_CONFIG,
} from "./constants";
import INTENT_PROJECT_HISTORY_TABLE_CELLS from "./Cells";
import styles from "./styles.module.css";

type IntentProjectHistoryTableProps = {
  operations: IntentResourceOperation[];
  loading: boolean;
  startCursor: string | undefined;
  endCursor: string | undefined;
  allItemsCount: number;
  resetKey?: unknown;
};

const IntentProjectHistoryTable = ({
  operations,
  loading,
  startCursor,
  endCursor,
  allItemsCount,
  resetKey,
}: IntentProjectHistoryTableProps) => {
  const { onFetchItems } = useIntentProjectOperationsStore();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: onFetchItems,
    startCursor,
    endCursor,
    allItemsCount,
    visibleItemsCount: operations.length,
    initialLimit: INTENT_PROJECT_HISTORY_LIMIT,
    storageKey: INTENT_PROJECT_HISTORY_LIMIT_STORAGE_KEY,
    resetKey,
    loading,
  });

  const hasPendingReview = useMemo(
    () =>
      operations.some(
        (operation) => operation.state === IntentResourceOperationState.PendingReview
      ),
    [operations]
  );

  return (
    <>
      <TableSimple
        data={operations}
        store={useIntentProjectHistoryTableStore}
        cellPerColumn={INTENT_PROJECT_HISTORY_TABLE_CELLS}
        ariaLabel="Intent Project Operations table"
        itemIdKey="id"
        filtersStorageKey={INTENT_PROJECT_HISTORY_TABLE_FILTERS_STORAGE_KEY}
        loading={loading}
        hasPagination
        sortConfig={INTENT_PROJECT_HISTORY_SORT_CONFIG}
        wrapperClassName={cx(styles.tableWrapper, hasPendingReview && styles.withReviewAction)}
      />

      <TablePagination
        from={from}
        to={to}
        loading={loading}
        goToNextPage={goToNextPage}
        setRowsPerPage={setRowsPerPage}
        goToPrevPage={goToPrevPage}
        goToFirstPage={goToFirstPage}
        goToLastPage={goToLastPage}
        rowsPerPage={rowsPerPage}
        itemsCount={allItemsCount}
        lastPage={lastPage}
        page={page}
        paginationOptions={INTENT_PROJECT_HISTORY_PAGINATION_OPTIONS}
      />
    </>
  );
};

export default IntentProjectHistoryTable;
